import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { PlanLike } from "@/lib/productionTierAccess";

/**
 * Resolve whether the current user may use a tier-gated feature on a specific
 * production. Owners are gated on their own plan; a co-producer inherits the
 * owning producer's plan (fetched once per briefing via
 * get_briefing_producer_subscription and shared through React Query's cache).
 *
 * `allows` is the pure predicate for the feature: hasLiveMapAccess from
 * @produce/shared for Live Tracking (the same rule the crew app uses),
 * planAllowsAnalytics from productionTierAccess.ts for Analytics.
 *
 * `scope` decides WHOSE plan counts for a co-producer:
 * - "viewer-or-owner" (default, the historical behaviour): the feature
 *   unlocks if the viewer's OWN plan allows it, or the owner's does.
 * - "briefing": only the plan that governs THIS briefing counts — the
 *   owner's. Use this for anything the crew_locations RLS enforces
 *   (Lock B): there the database follows the briefing's producer, and a
 *   co-producer with a personal PRO plan on a LITE owner's production
 *   would otherwise see a "ready" UI polling for rows the database
 *   never returns.
 */
export function useProductionTierAccess(
  briefingId: string | undefined,
  allows: (plan: PlanLike | null | undefined) => boolean,
  scope: "viewer-or-owner" | "briefing" = "viewer-or-owner",
): { hasAccess: boolean; isLoading: boolean } {
  const { subscriptionStatus, isSubscriptionLoading, isCoProducer } = useAuth();

  const { data: ownerPlan, isLoading: isOwnerPlanLoading } = useQuery({
    queryKey: ["briefing-owner-plan", briefingId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_briefing_producer_subscription", {
        p_briefing_id: briefingId!,
      });
      if (error) throw error;
      return data?.[0] ?? null;
    },
    enabled: isCoProducer && !!briefingId,
  });

  const hasAccess =
    scope === "briefing" && isCoProducer
      ? allows(ownerPlan)
      : allows(subscriptionStatus) || (isCoProducer && allows(ownerPlan));
  const isLoading = isSubscriptionLoading || (isCoProducer && isOwnerPlanLoading);

  return { hasAccess, isLoading };
}
